import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'

import { Button, ButtonText, CardDashboard, Spacer } from '@percent/lemonade'
import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { Organisation, PaymentMethod } from '@percent/admin-dashboard/api/types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { usePermissions, useQuery } from '@percent/admin-dashboard/common/hooks'
import styles from './PaymentDetails.module.scss'
import { BankDetailsView } from './BankDetailsView'
import { CardView } from './CardView'
import { PaymentTypeWithButton } from './PaymentTypeWithButton'
import { AddBankDetailsModal } from './AddBankDetailsModal'
import { RemoveBankDetailsModal } from './RemoveBankDetailsModal'
import { MarkOrganisationAsUnpayableModal } from './MarkOrganisationAsUnpayableModal'
import { UnableToPayView } from './UnableToPayView'
import { BankDetailStatus } from '@percent/domain/bank-details'
import { CreationDetails } from './CreationDetails'

export function PaymentDetails({
  organization,
  paymentMethod,
  paymentMethodRefresh
}: Readonly<{
  organization: Organisation
  paymentMethod?: PaymentMethod
  paymentMethodRefresh: VoidFunction
}>) {
  const [openAddBankDetailsModal, setOpenAddBankDetailsModal] = useState(false)
  const [openRemoveBankDetailsModal, setOpenRemoveBankDetailsModal] = useState(false)
  const [openMarkOrganisationAsUnpayableModal, setOpenMarkOrganisationAsUnpayableModal] = useState(false)

  const { t } = useTranslation()
  const bankDetailsReadFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_BANK_DETAILS_READ)
  const bankDetailsSubmitFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_BANK_DETAILS_SUBMIT)
  const bankDetailsReviewDeleteFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_BANK_DETAILS_REVIEW_DELETE)
  const markOrganisationAsUnableToPayFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.AD_MARK_ORGANISATION_UNPAYABLE
  )
  const internalFgaDoraBankDetailsPaymentMethodsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_BANK_DETAILS_PAYMENT_METHODS
  )
  const { bankDetailsService } = useServices()
  const { userHasPermissions } = usePermissions()

  const canMarkOrgUnpayable =
    !internalFgaDoraBankDetailsPaymentMethodsFeatureFlag || userHasPermissions('bank_details_payment_methods:can_set')
  const canRemoveBankDetails =
    !internalFgaDoraBankDetailsPaymentMethodsFeatureFlag ||
    userHasPermissions('bank_details_payment_methods:can_delete')

  const [{ data: bankDetailsData, isLoading: bankDetailsLoading, errorMessage: bankDetailsError }, { refresh }] =
    useQuery(bankDetailsService.getBankDetails, { organisationId: organization.id })

  const handleRemoveBankDetailsModal = () => {
    setOpenRemoveBankDetailsModal(prevState => !prevState)
  }

  const handleMarkOrganisationAsUnpayableModal = () => {
    setOpenMarkOrganisationAsUnpayableModal(prevState => !prevState)
  }

  const handleAddBankDetailsModal = () => {
    setOpenAddBankDetailsModal(prevState => !prevState)
  }

  if (bankDetailsLoading) {
    return <Loader />
  }

  if (bankDetailsError) {
    return <ErrorView errorMessage={bankDetailsError} />
  }

  const isBankDetailsAvailable =
    bankDetailsData?.status === BankDetailStatus.APPROVED || bankDetailsData?.status === BankDetailStatus.PENDING

  const paymentDetailsComponents: JSX.Element[] = []

  if (paymentMethod?.method === 'card') {
    paymentDetailsComponents.push(
      <CardView
        paymentMethod={paymentMethod}
        refresh={paymentMethodRefresh}
        organisationCountryCode={organization.countryCode}
      />
    )

    paymentMethod && paymentDetailsComponents.push(<CreationDetails paymentMethod={paymentMethod} />)
  }

  if (paymentMethod?.method === 'cheque') {
    paymentDetailsComponents.push(
      <PaymentTypeWithButton
        title={t('typography.paymentMethod')}
        text={t('typography.cheque')}
        paymentMethod={paymentMethod}
        refresh={paymentMethodRefresh}
        organisationCountryCode={organization.countryCode}
      />
    )

    paymentMethod && paymentDetailsComponents.push(<CreationDetails paymentMethod={paymentMethod} />)
  }

  if (paymentMethod?.method === 'unable_to_pay') {
    paymentDetailsComponents.push(
      <UnableToPayView
        paymentMethod={paymentMethod}
        refresh={paymentMethodRefresh}
        organisationCountryCode={organization.countryCode}
      />
    )
    paymentMethod && paymentDetailsComponents.push(<CreationDetails paymentMethod={paymentMethod} />)
  }

  if (bankDetailsReadFeatureFlag && (isBankDetailsAvailable || !paymentDetailsComponents.length)) {
    const previousLength = paymentDetailsComponents.length

    paymentDetailsComponents.length &&
      paymentDetailsComponents.push(<div className={styles.spacerWithLine} />, <Spacer size={6} axis="vertical" />)

    paymentDetailsComponents.push(
      <BankDetailsView
        paymentMethod={paymentMethod}
        paymentMethodRefresh={paymentMethodRefresh}
        bankDetailsData={bankDetailsData}
        organisationCountryCode={organization.countryCode}
      />
    )

    !previousLength && paymentMethod && paymentDetailsComponents.push(<CreationDetails paymentMethod={paymentMethod} />)
  }

  if (!paymentDetailsComponents.length) {
    paymentDetailsComponents.push(
      <PaymentTypeWithButton
        title={t('typography.paymentMethod')}
        text={isBankDetailsAvailable ? t('typography.bankTransfer') : t('typography.noneSupplied')}
        paymentMethod={paymentMethod}
        refresh={paymentMethodRefresh}
        isUpdateAvailable={!isBankDetailsAvailable}
        organisationCountryCode={organization.countryCode}
      />
    )
  }

  const action = (() => {
    if (bankDetailsData?.status === BankDetailStatus.APPROVED && bankDetailsReviewDeleteFeatureFlag) {
      return (
        <Button
          onClick={handleRemoveBankDetailsModal}
          variant="secondary"
          data-testid="bank-details-action-button"
          disabled={!canRemoveBankDetails}
        >
          {t('typography.removeBankDetails')}
        </Button>
      )
    }

    if (
      (!bankDetailsData?.bankAccountDetails || bankDetailsData?.status === BankDetailStatus.REJECTED) &&
      bankDetailsSubmitFeatureFlag
    ) {
      return (
        <Button onClick={handleAddBankDetailsModal} variant="secondary" data-testid="bank-details-action-button">
          {t('typography.addBankDetails')}
        </Button>
      )
    }

    return null
  })()

  return (
    <>
      <CardDashboard title={t('typography.paymentDetails')} action={action}>
        {markOrganisationAsUnableToPayFeatureFlag && (
          <>
            <Box className={styles.markOrgAsUnableToPay}>
              {paymentMethod?.method === 'unable_to_pay' ? (
                <Typography variant="body2">{t('typography.markedAsUnableToPayText')}</Typography>
              ) : (
                <>
                  <Typography variant="body2">{t('typography.unableToPayText')}&nbsp;</Typography>
                  <ButtonText
                    onClick={handleMarkOrganisationAsUnpayableModal}
                    variant="primary"
                    disabled={!canMarkOrgUnpayable}
                    data-testid="open-unable-to-pay-modal-button"
                  >
                    {t('button.markAsUnableToPay')}
                  </ButtonText>
                  .
                </>
              )}
            </Box>
            <Spacer size={6} axis="vertical" />
          </>
        )}
        {paymentDetailsComponents}
      </CardDashboard>
      <Spacer size={6} axis="vertical" />
      <AddBankDetailsModal
        open={openAddBankDetailsModal}
        onClose={handleAddBankDetailsModal}
        refresh={refresh}
        bankDetailsData={bankDetailsData}
      />
      <RemoveBankDetailsModal
        open={openRemoveBankDetailsModal}
        onClose={handleRemoveBankDetailsModal}
        refresh={paymentMethodRefresh}
      />
      <MarkOrganisationAsUnpayableModal
        open={openMarkOrganisationAsUnpayableModal}
        onClose={handleMarkOrganisationAsUnpayableModal}
        refresh={paymentMethodRefresh}
      />
    </>
  )
}
